import { useEffect, useRef, useState } from 'react';

const WINDOW_OPTIONS = [10, 15, 20, 30, 45];
const PX_PER_SEC = 14;

function seededBars(seed, count) {
  let a = 0;
  for (let i = 0; i < seed.length; i += 1) a = (a * 31 + seed.charCodeAt(i)) >>> 0;
  const out = [];
  for (let i = 0; i < count; i += 1) {
    a = (a * 1664525 + 1013904223) >>> 0;
    const n = a / 4294967296;
    const pulse = 0.35 + 0.65 * Math.abs(Math.sin(i * 0.37 + n * 6));
    out.push(0.18 + pulse * (0.35 + n * 0.65));
  }
  return out;
}

function peaksFromBuffer(buf, count) {
  try {
    const ch = buf.getChannelData(0);
    const n = Math.max(1, count);
    const block = Math.max(1, Math.floor(ch.length / n));
    const peaks = [];
    for (let i = 0; i < n; i += 1) {
      let max = 0;
      const start = i * block;
      for (let j = 0; j < block; j += 12) {
        const v = Math.abs(ch[start + j] || 0);
        if (v > max) max = v;
      }
      peaks.push(Math.max(0.16, Math.min(1, max * 1.85)));
    }
    return peaks;
  } catch {
    return [];
  }
}

function fmt(sec) {
  const s = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function waitForSeek(audio, time) {
  const t = Math.max(0, Number(time) || 0);
  return new Promise((resolve) => {
    if (audio.readyState >= 1 && Math.abs((audio.currentTime || 0) - t) < 0.05) {
      resolve();
      return;
    }
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      audio.removeEventListener('seeked', finish);
      audio.removeEventListener('canplay', finish);
      resolve();
    };
    audio.addEventListener('seeked', finish);
    audio.addEventListener('canplay', finish);
    const apply = () => {
      try {
        audio.currentTime = t;
      } catch {
        finish();
      }
    };
    if (audio.readyState >= 1) apply();
    else {
      audio.addEventListener('loadedmetadata', apply, { once: true });
      try {
        audio.load();
      } catch {
        finish();
      }
    }
    window.setTimeout(finish, 600);
  });
}

export default function SongScrubber({ url, start, windowSec, onChange, songDuration, onDuration }) {
  const scrollerRef = useRef(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const bufferRef = useRef(null);
  const sourceRef = useRef(null);
  const dragging = useRef(false);
  const mouseDrag = useRef(false);
  const dragX = useRef(0);
  const dragScroll = useRef(0);
  const syncing = useRef(false);
  const seeking = useRef(false);
  const ignorePause = useRef(false);
  const raf = useRef(0);
  const startRef = useRef(0);
  const winRef = useRef(15);
  const ppsRef = useRef(PX_PER_SEC);
  const durRef = useRef(0);
  const playingRef = useRef(false);
  const [peaks, setPeaks] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [boxW, setBoxW] = useState(160);
  const [pad, setPad] = useState(80);
  const [pps, setPps] = useState(PX_PER_SEC);
  const win = Math.max(5, Number(windowSec) || 15);

  startRef.current = Number(start) || 0;
  winRef.current = win;
  ppsRef.current = pps;
  durRef.current = Number(songDuration) || 0;
  playingRef.current = playing;

  const dur = Number(songDuration) || 0;
  const barsW = Math.max(win, dur || win) * pps;
  const barCount = Math.max(80, Math.floor(barsW / 4));

  function startFromScroller() {
    const node = scrollerRef.current;
    const duration = durRef.current;
    const windowLen = winRef.current;
    const maxStart = Math.max(0, duration - windowLen);
    if (!node) return Math.max(0, Math.min(maxStart, startRef.current));
    return Math.max(0, Math.min(maxStart, node.scrollLeft / ppsRef.current));
  }

  function emitStart(nextStart) {
    const duration = durRef.current;
    const windowLen = winRef.current;
    const maxStart = Math.max(0, duration - windowLen);
    const from = Math.max(0, Math.min(maxStart, nextStart));
    const to = duration ? Math.min(duration, from + windowLen) : from + windowLen;
    startRef.current = from;
    onChange?.(from, to);
    return from;
  }

  function stopSource() {
    const src = sourceRef.current;
    sourceRef.current = null;
    if (!src) return;
    try {
      src.stop();
    } catch {
      /* already stopped */
    }
    try {
      src.disconnect();
    } catch {
      /* already disconnected */
    }
  }

  function stopAll() {
    stopSource();
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      ignorePause.current = true;
      audio.pause();
    }
  }

  async function playAt(from) {
    const ctx = ctxRef.current;
    const buf = bufferRef.current;
    const windowLen = winRef.current;

    if (ctx && buf) {
      stopAll();
      if (ctx.state === 'suspended') await ctx.resume();
      const off = Math.min(Math.max(0, from), Math.max(0, buf.duration - 0.05));
      const end = Math.min(buf.duration, off + windowLen);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.loop = true;
      src.loopStart = off;
      src.loopEnd = Math.max(off + 0.05, end);
      src.connect(ctx.destination);
      src.start(0, off);
      sourceRef.current = src;
      setPlaying(true);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;
    stopSource();
    seeking.current = true;
    try {
      await waitForSeek(audio, from);
      await audio.play();
      if (Math.abs((audio.currentTime || 0) - from) > 0.3) {
        audio.currentTime = from;
      }
      setPlaying(true);
    } catch {
      setPlaying(false);
    } finally {
      window.setTimeout(() => {
        seeking.current = false;
      }, 80);
    }
  }

  useEffect(() => {
    let alive = true;
    stopAll();
    setPlaying(false);
    bufferRef.current = null;
    if (!url) {
      setPeaks([]);
      return undefined;
    }
    setPeaks(seededBars(url, 96));
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    (async () => {
      try {
        const res = await fetch(url);
        const raw = await res.arrayBuffer();
        const buf = await ctx.decodeAudioData(raw.slice(0));
        if (!alive || ctxRef.current !== ctx) return;
        bufferRef.current = buf;
        onDuration?.(buf.duration);
        setPeaks(peaksFromBuffer(buf, Math.max(80, Math.round(buf.duration * 8))));
      } catch {
        /* HTML audio fallback still works */
      }
    })();
    return () => {
      alive = false;
      stopAll();
      if (ctxRef.current === ctx) ctxRef.current = null;
      bufferRef.current = null;
      ctx.close().catch(() => {});
    };
    // decode once per file; peaks scale from that buffer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    const buf = bufferRef.current;
    if (!buf) return;
    setPeaks(peaksFromBuffer(buf, barCount));
  }, [barCount]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node) return undefined;
    const measure = () => {
      const w = node.clientWidth || 320;
      const nextBox = Math.max(108, Math.min(w * 0.5, 220));
      setBoxW(nextBox);
      setPad(Math.max(20, (w - nextBox) / 2));
      setPps(nextBox / win);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, [win, url]);

  useEffect(() => {
    const node = scrollerRef.current;
    if (!node || dragging.current) return;
    syncing.current = true;
    node.scrollLeft = Math.max(0, Number(start) || 0) * pps;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  }, [start, pad, boxW, songDuration, pps]);

  function emitFromScroll(scrollLeft) {
    emitStart(scrollLeft / ppsRef.current);
  }

  function onScroll(e) {
    if (syncing.current) return;
    const sl = e.currentTarget.scrollLeft;
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      emitFromScroll(sl);
    });
  }

  function cycleWindow() {
    const duration = durRef.current || 60;
    const idx = WINDOW_OPTIONS.findIndex((n) => n >= win);
    const next = WINDOW_OPTIONS[(Math.max(0, idx) + 1) % WINDOW_OPTIONS.length];
    const clipped = Math.min(next, Math.max(5, Math.floor(duration) || next));
    const maxStart = Math.max(0, duration - clipped);
    const nextStart = Math.min(startFromScroller(), maxStart);
    startRef.current = nextStart;
    onChange?.(nextStart, nextStart + clipped, clipped);
    if (playingRef.current) playAt(nextStart);
  }

  function togglePlay() {
    if (playingRef.current) {
      stopAll();
      setPlaying(false);
      return;
    }
    const from = emitStart(startFromScroller());
    playAt(from);
  }

  function resumeFromWindow() {
    const from = emitStart(startFromScroller());
    if (playingRef.current) playAt(from);
  }

  const startSec = Number(start) || 0;
  const selRatio = dur > 0 ? win / dur : 1;
  const leftRatio = dur > 0 ? startSec / dur : 0;

  return (
    <div className="reel-scrub">
      <audio
        ref={audioRef}
        src={url}
        preload="auto"
        onLoadedMetadata={(e) => {
          if (!bufferRef.current) onDuration?.(e.currentTarget.duration || 0);
        }}
        onTimeUpdate={(e) => {
          if (seeking.current || sourceRef.current) return;
          const from = startRef.current;
          const to = from + winRef.current;
          const t = e.currentTarget.currentTime || 0;
          if (t < from - 0.12) {
            e.currentTarget.currentTime = from;
            return;
          }
          if (t >= to - 0.04) e.currentTarget.currentTime = from;
        }}
        onEnded={() => {
          if (sourceRef.current) return;
          const audio = audioRef.current;
          if (!audio) return;
          audio.currentTime = startRef.current;
          audio.play().catch(() => setPlaying(false));
        }}
        onPause={() => {
          if (ignorePause.current || seeking.current || sourceRef.current) {
            ignorePause.current = false;
            return;
          }
          setPlaying(false);
        }}
      />

      <div className="reel-scrub-top">
        <button type="button" className="reel-scrub-len" onClick={cycleWindow} title="Clip length">
          {Math.round(win)}
        </button>
        <div className="reel-scrub-mini" aria-hidden="true">
          <span
            className="reel-scrub-mini-sel"
            style={{ left: `${leftRatio * 100}%`, width: `${Math.max(4, selRatio * 100)}%` }}
          />
        </div>
        <button
          type="button"
          className="reel-scrub-play"
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
        >
          <i className={`pi ${playing ? 'pi-pause' : 'pi-play'}`} />
        </button>
      </div>

      <div className="reel-scrub-stage">
        <div className="reel-scrub-window" style={{ width: boxW }} />
        <div
          ref={scrollerRef}
          className="reel-scrub-scroller"
          onScroll={onScroll}
          onPointerDown={(e) => {
            dragging.current = true;
            if (e.pointerType !== 'mouse') return;
            mouseDrag.current = true;
            dragX.current = e.clientX;
            dragScroll.current = scrollerRef.current?.scrollLeft || 0;
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (!mouseDrag.current || !scrollerRef.current) return;
            scrollerRef.current.scrollLeft = dragScroll.current + (dragX.current - e.clientX);
          }}
          onPointerUp={() => {
            dragging.current = false;
            mouseDrag.current = false;
            resumeFromWindow();
          }}
        >
          <div className="reel-scrub-track" style={{ paddingLeft: pad, paddingRight: pad }}>
            <div className="reel-scrub-bars" style={{ width: barsW }}>
              {(peaks.length ? peaks : seededBars(url || 'song', barCount)).map((h, i) => (
                <span
                  key={i}
                  className="reel-scrub-bar"
                  style={{ height: `${Math.round(14 + (Number(h) || 0.2) * 34)}px` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="reel-scrub-meta">
        {dur
          ? `${fmt(startSec)} – ${fmt(Math.min(dur, startSec + win))} of ${fmt(dur)}`
          : 'Drag to pick the part of the song'}
      </p>
    </div>
  );
}
